import tmplHistorySuggestContainer from "@/../templates/pc/container/historySuggestContainer.html?raw";
import tmplWordSuggestContainer from "@/../templates/pc/container/wordSuggestContainer.html?raw";
import tmplSuggest from "@/../templates/pc/suggest.html?raw";
import { buildCategorySearchPageUrl, buildDisplayProductName, buildMakerSearchPageUrl, buildProductDetailPageUrl, buildTagCodeSearchPageUrl, createEventHandlerAdapter, joinCategory, onClickDeleteHistory, splitCategory } from "./common";

/** @type {string} 履歴を削除するボタンのセレクタ */
const deleteHistoryButtonSelector = 'a[data-type="delete-history"]';

/**
 * PC版画面向けの固有処理を行うハンドラーを生成する。
 *
 * @returns {UIHandler}
 */
export function createUIHandlerPC() {
  return {
    renderWordSuggestRoot,
    renderWordSuggests,
    renderHistorySuggestRoot,
    renderHistorySuggests,
    moveActiveSuggest,
    resolveDisplayKeyword,
    selectSuggest,
  };
}

/**
 * ワードサジェストのルート要素を描画する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} target サジェストのルート要素
 * @returns {void}
 */
function renderWordSuggestRoot(context, target) {
  const root = $(tmplWordSuggestContainer);
  target.addClass('search-suggest')
    .empty()
    .append(root);
}

/**
 * ワードサジェストを描画する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} root サジェストのルート要素
 * @param {string} keyword 検索キーワード
 * @param {UniSuggestApiResponse} response ユニサジェストAPIレスポンス
 * @returns {void}
 */
function renderWordSuggests(context, root, keyword, response) {
  // キーワードサジェスト
  const keywordSuggestList = root.find('.keyword');
  keywordSuggestList.empty();
  response.response?.keyword?.docs?.item?.forEach(item => {
    buildSuggestElement(context, item.word)
      .appendTo(keywordSuggestList);
  });

  // 品番サジェスト
  const itemNumberSuggestList = root.find('.item-number');
  itemNumberSuggestList.empty();
  response.response?.itemnumber?.docs?.item?.forEach(item => {
    const displayProductName = buildDisplayProductName(item, false);
    const url = buildProductDetailPageUrl(context.config.searchAppBaseUrl, item.direct_product_no);
    buildSuggestElement(context, displayProductName, url)
      .appendTo(itemNumberSuggestList);
  });

  // 型番サジェスト
  const modelNumberSuggestList = root.find('.model-number');
  modelNumberSuggestList.empty();
  response.response?.modelnumber?.docs?.item?.forEach(item => {
    const displayProductName = buildDisplayProductName(item, false);
    const url = buildProductDetailPageUrl(context.config.searchAppBaseUrl, item.direct_product_no);
    buildSuggestElement(context, displayProductName, url)
      .appendTo(modelNumberSuggestList);
  });

  // 商品名サジェスト
  const productNameSuggestList = root.find('.product-name');
  productNameSuggestList.empty();
  response.response?.productname?.docs?.item?.forEach(item => {
    const displayProductName = buildDisplayProductName(item, true);
    const url = buildProductDetailPageUrl(context.config.searchAppBaseUrl, item.direct_product_no);
    buildSuggestElement(context, displayProductName, url)
      .appendTo(productNameSuggestList);
  });

  // カテゴリサジェスト
  const categorySuggestList = root.find('.category');
  categorySuggestList.empty();
  response.response?.category?.docs?.item?.forEach(item => {
    const idList = splitCategory(item.direct_category_id);
    const lastCategoryId = idList[idList.length - 1];
    const nameList = splitCategory(item.direct_category_name);
    const lastCategoryName = nameList.pop();
    if (!lastCategoryName) {
      return;
    }
    const displayCategoryName = nameList.length
      ? `${lastCategoryName} [${joinCategory(nameList)}]`
      : lastCategoryName;
    const url = buildCategorySearchPageUrl(context.config.searchAppBaseUrl, lastCategoryId);
    buildSuggestElement(context, displayCategoryName, url)
      .appendTo(categorySuggestList);
  });

  // メーカーサジェスト
  const makerSuggestList = root.find('.maker');
  makerSuggestList.empty();
  response.response?.maker?.docs?.item?.forEach(item => {
    const url = buildMakerSearchPageUrl(context.config.searchAppBaseUrl, item.direct_maker_id);
    buildSuggestElement(context, item.direct_maker_name, url)
      .appendTo(makerSuggestList);
  });

  // キャンペーンハッシュタグサジェスト
  const campaignHashTagSuggestList = root.find('.tag');
  campaignHashTagSuggestList.empty();
  response.response?.campaigntag?.docs?.item?.forEach(item => {
    const url = buildTagCodeSearchPageUrl(context.config.searchAppBaseUrl, item.direct_campaign_id);
    buildSuggestElement(context, item.direct_campaign_tag, url)
      .appendTo(campaignHashTagSuggestList);
  });
}

/**
 * 履歴サジェストのルート要素を描画する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} target 履歴サジェストのルート要素
 * @returns {void}
 */
function renderHistorySuggestRoot(context, target) {
  const root = $(tmplHistorySuggestContainer);
  target.addClass('history-suggest')
    .empty()
    .append(root);

  // 履歴サジェストの削除ボタン
  target.find(deleteHistoryButtonSelector)
    .on('click', createEventHandlerAdapter(onClickDeleteHistory, context));
}

/**
 * 履歴サジェストを描画する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} root サジェストのルート要素
 * @param {UniSuggestApiResponse} response ユニサジェストAPIレスポンス
 * @returns {void}
 */
function renderHistorySuggests(context, root, response) {
  const clearSuggest = root.find(deleteHistoryButtonSelector).closest('li');
  response.response?.history?.docs?.item?.forEach(item => {
    const suggest = buildSuggestElement(context, item.word);
    clearSuggest.before(suggest);
  });
}

/**
 * サジェストのDOM要素を構築する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {string} text 表示テキスト
 * @param {string} [url] URL
 * @returns {JQuery<HTMLElement>}
 */
function buildSuggestElement(context, text, url) {
  const suggest = $(tmplSuggest);
  const link = suggest.find('a');
  link.text(text);
  if (url) {
    link.attr('href', url);
  } else {
    link.attr('href', 'javascript:void(0);')
      .on('click', function (event) {
        $(context.config.triggerTarget).val(text);
        $(this).closest('form').trigger('submit');
      });
  }
  return suggest;
}

/**
 * 選択中のサジェスト変更を行う。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} root サジェストのルート要素
 * @param {SuggestMoveDirection} moveDirection 移動方向
 * @returns {JQuery<HTMLElement>} 切り替え後のサジェスト要素
 */
function moveActiveSuggest(context, root, moveDirection) {
  const suggestList = root.find('li');
  const activeSuggest = suggestList.filter((index, element) => $(element).hasClass('active'));
  let nextActiveSuggest;
  if (!activeSuggest.length) {
    if (moveDirection < 0) {
      nextActiveSuggest = suggestList.last();
    } else {
      nextActiveSuggest = suggestList.first();
    }
  } else {
    activeSuggest.removeClass('active')

    const activeSuggestIndex = suggestList.index(activeSuggest);
    const nextActiveSuggestIndex = activeSuggestIndex + moveDirection;
    if (nextActiveSuggestIndex < 0 || suggestList.length <= nextActiveSuggestIndex) {
      nextActiveSuggest = $();
    } else {
      nextActiveSuggest = suggestList.eq(nextActiveSuggestIndex);
    }
  }
  if (nextActiveSuggest.length) {
    nextActiveSuggest.addClass('active');
  }
  return nextActiveSuggest;
}

/**
 * サジェストから画面表示用検索キーワードを取得する。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} suggest サジェストのルート要素
 * @returns {string} 画面表示用検索キーワード
 */
function resolveDisplayKeyword(context, suggest) {
  if (suggest.length && !suggest.find(deleteHistoryButtonSelector).length) {
    const keyword = suggest.find('a').text();
    return keyword;
  } else {
    return '';
  }
}

/**
 * サジェスト選択処理を行う。
 *
 * @param {SuggestContext} context コンテキスト
 * @param {JQuery<HTMLElement>} suggest サジェスト要素
 */
function selectSuggest(context, suggest) {
  // NOTE: triggerメソッドでは画面遷移しないため、DOM APIを直接呼び出す
  const link = suggest.find('a');
  // link.trigger('click');
  link[0].click();
}
